import { useState } from "react";
import { generateFinanceData, Transaction, StatementLine, channelToSource } from "./finance-data";

// State shared by the Finances pages (reconciliation changes show up in transactions and reports).
export interface FinanceStore {
  txs: Transaction[];
  setTxs: React.Dispatch<React.SetStateAction<Transaction[]>>;
  statement: StatementLine[];
  links: Record<string, string>; // statementId -> transactionId
  setLinks: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  // Statement lines handled without a match: justified gap or dispute.
  notes: Record<string, { kind: "Écart justifié" | "Litige"; text: string }>;
  setNotes: React.Dispatch<React.SetStateAction<Record<string, { kind: "Écart justifié" | "Litige"; text: string }>>>;
}

export function useFinanceStore(): FinanceStore {
  const [init] = useState(generateFinanceData);
  const [txs, setTxs] = useState(init.txs);
  const [links, setLinks] = useState(init.links);
  const [notes, setNotes] = useState<FinanceStore["notes"]>({});
  return { txs, setTxs, statement: init.statement, links, setLinks, notes, setNotes };
}

// Electronic, successful transactions are the ones expected on an operator statement.
export const expectsStatement = (t: Transaction) => t.status === "Réussie" && channelToSource(t.channel) !== null;

export const reconciledTxIds = (s: FinanceStore) => new Set(Object.values(s.links));

export function reconciliationStats(s: FinanceStore) {
  const linked = reconciledTxIds(s);
  const expected = s.txs.filter(expectsStatement);
  const byRef = new Map(s.txs.map((t) => [t.opRef, t]));
  const openLines = s.statement.filter((l) => !s.links[l.id] && !s.notes[l.id]);
  const gaps = openLines.filter((l) => {
    const t = byRef.get(l.opRef);
    return t && !linked.has(t.id) && t.amount !== l.amount;
  }).length;
  const unidentified = openLines.filter((l) => !byRef.get(l.opRef)).length;
  const statementRefs = new Set(s.statement.map((l) => l.opRef));
  const missing = expected.filter((t) => !linked.has(t.id) && !statementRefs.has(t.opRef)).length;
  const handled = s.statement.filter((l) => s.links[l.id] || s.notes[l.id]).length;
  const rate = s.statement.length ? Math.round((handled / s.statement.length) * 100) : 100;
  return { rate, gaps, unidentified, missing, pendingAuto: openLines.filter((l) => { const t = byRef.get(l.opRef); return t && !linked.has(t.id) && t.amount === l.amount; }).length };
}
